import React, { useMemo, useState } from "react";
import { Card, CardBody, Button, Collapse, Col } from "reactstrap";
import ConfirmationModal from "./ConfirmationModal";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { useAppData } from "../context";
import DeviceImage from "./DeviceImage";
import deviceIdleSvg from "../assets/device-idle.svg";
import deviceOptiSvg from "../assets/device-opti.svg";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateSiteSteering } from "../api/usersApi";
import SteeringModeSelector from "./SteeringModeSelector";
import WarningAlert from "./WarningAlert";

const formatRelativeTime = (ts) =>
  ts ? formatDistanceToNow(new Date(ts), { addSuffix: true }) : "--";

export default function SteeringOverviewBlock({ siteId, preferences, preferenceType }) {
  const { sites } = useAppData();
  const site = sites.find((s) => s.id === siteId);
  const { t } = useTranslation();
  const timeout = parseInt(window.appConfig?.getTimeout);

  const isIdle = preferenceType === "Idle";
  const tradingId = useMemo(() => {
    const tradingPref = preferences?.find(p => p.preference_type === "Trading");
    return tradingPref?.preferences?.id ?? null;
  }, [preferences]);

  const statusText = isIdle ? t("SteeringOverviewBlock-Inactive") : t("SteeringOverviewBlock-Active");

  // enum -> UUID map (for update_preferred)
  const idByEnum = useMemo(() => {
    const map = {};
    (preferences || []).forEach((p) => {
      if (p?.preference_type && p?.id) map[p.preference_type] = p.id;
    });
    return map;
  }, [preferences]);

  const preferredEnum = useMemo(
    () => preferences?.find((p) => p.id === site?.preferred_steering_mode)?.preference_type,
    [preferences, site?.preferred_steering_mode]
  );
  const preferredLabel = preferredEnum ? t(`SteeringPref.${preferredEnum}`) : t("SteeringPref.SteeringOverviewBlock-modal-error-message-line-2");

  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // "toggle" | "error" | "pick"
  const [modalKind, setModalKind] = useState("toggle");
  const [pendingPickEnum, setPendingPickEnum] = useState(null);
  const [updateError, setUpdateError] = useState("");

  const healthAge = useMemo(() => {
    const h = site.health || [];
    return h.length > 0 && typeof h[0].time === "string" ? formatRelativeTime(h[0].time) : t("N/A");
  }, [site.health, t]);

  const deviceSvgPath = isIdle ? deviceIdleSvg : deviceOptiSvg;

  const qc = useQueryClient();

  const setSteeringMutation = useMutation({
    mutationFn: updateSiteSteering,
    onSuccess: () => {
      qc.invalidateQueries(["customer_blob"]);
      setModalOpen(false);
      setUpdateError("");
    },
    onError: (err) => {
      setUpdateError(err?.message || String(err));
      setModalOpen(false);
    },
  });

  // Toggle flow
  function openToggleModal() {
    if (isIdle && !preferredEnum) {
      // cannot turn ON: no preferred configured in backend
      setModalKind("error");
      setModalOpen(true);
      return;
    }
    setModalKind("toggle");
    setModalOpen(true);
  }

  function confirmToggle() {
    const targetPref = isIdle ? site?.preferred_steering_mode : idByEnum["Idle"];
    setSteeringMutation.mutate({ queryKey: [null, site.id, targetPref, timeout] });
  }

  function handleAttemptPick(enumString) {
    const prefId = idByEnum[enumString];
    setPendingPickEnum(enumString);
    if (!prefId) {
      // chosen mode not present for this site -> show error modal, no selection change
      setModalKind("error");
      setModalOpen(true);
      return false; // child won't highlight
    }
    setModalKind("pick");
    setModalOpen(true);
    return false; // wait for confirm
  }

  // Confirm the preferred mode change
  function confirmPick() {
    if (!pendingPickEnum) {
      setModalOpen(false);
      return;
    }
    const prefId = idByEnum[pendingPickEnum];
    setModalOpen(false);
    setPendingPickEnum(null);
    setSteeringMutation.mutate({
      queryKey: [null, site.id, prefId, timeout],
    });
  }

  // Modal content
  const pendingPickLabel = pendingPickEnum ? t(`SteeringPref.${pendingPickEnum}`) : "";

  const modalTitle =
    modalKind === "error"
      ? t("SteeringOverviewBlock-modal-prompt-activation")
      : modalKind === "pick"
        ? t("SteeringPref.ChangePreferred")
        : isIdle
          ? t("SteeringOverviewBlock-modal-prompt-activation")
          : t("SteeringOverviewBlock-modal-prompt-deactivation");

  const modalMessage =
    modalKind === "error"
      ? t("SteeringOverviewBlock-modal-error-message-line-2", { mode: pendingPickLabel })
      : modalKind === "pick"
        ? t("SteeringPref.ConfirmChangeTo", { mode: pendingPickLabel })
        : isIdle
          ? `${t("SteeringOverviewBlock-modal-prompt-opti")} ${preferredLabel}?`
          : `${t("SteeringOverviewBlock-modal-prompt-idle")} Revolta One ?`;

  const confirmLabel =
    modalKind === "error"
      ? t("SteeringOverviewBlock-modal-OK")
      : modalKind === "pick"
        ? t("SteeringPref.SetPreferred")
        : isIdle
          ? t("SteeringOverviewBlock-modal-prompt-activate")
          : t("SteeringOverviewBlock-modal-prompt-deactivate");

  return (
    <Col className="px-0">
      <Card data-testid="card" className={`mb-3 shadow border-lt ${isOpen ? "expanded" : "collapsed"}`}>
        <CardBody className="p-2 cardbody-md-default">
          <div data-testid="status-row" className="status-row">
            <div className="status-cell status-cell-1">
              <span className="cell-text">
                {t("SteeringOverviewBlock-Welcome, ")}
                <span style={{ whiteSpace: "nowrap" }}> ReVolta One {t("SteeringOverviewBlock-is")}</span>
              </span>
            </div>

            <div className="status-cell status-cell-2">
              <div className={`status-badge-inner ${isIdle ? "bg-secondary text-white" : "bg-success text-white"}`}>
                <span className="cell-text">{statusText}</span>
              </div>
            </div>

            <div className="status-cell status-cell-3">
              <div className="cell-column">
                <span className="cell-text cell-title">{t("SteeringOverviewBlock-Last Checked")}</span>
                <span className="cell-text cell-sub" aria-live="polite">{healthAge}</span>
              </div>
            </div>

            <div className="status-cell status-cell-4">
              <Button
                color="link"
                onClick={() => setIsOpen((v) => !v)}
                className="status-chevron"
                data-testid="chevron"
                aria-expanded={isOpen}
              >
                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </Button>
            </div>
          </div>

          <Collapse isOpen={isOpen}>
            <div className="details-row mb-1">
              <div data-testid="details-rowA" className="details-rowA">
                <div className="details-cell details-cell-1">
                  <DeviceImage src={deviceSvgPath} alt="device" maxHeight={140} />
                </div>

                <div className="details-cell details-cell-2">
                  <label
                    data-testid="SteeringOverviewBlock-toggle"
                    className={`switch SteeringOverviewBlock-toggle ${isIdle ? "off" : "on"}`}
                    tabIndex={0}
                    aria-pressed={!isIdle}
                    aria-disabled={setSteeringMutation.isLoading}
                    title={t("Toggle")}
                    onClick={() => {
                      if (!setSteeringMutation.isLoading) openToggleModal();
                    }}
                  >
                    <span className="switch-track" />
                    <span className="switch-thumb" />
                  </label>
                </div>

                <div className="details-cell details-cell-3">
                  <span>
                    {isIdle
                      ? t("SteeringOverviewBlock-ClickToggleToActivate")
                      : t("SteeringOverviewBlock-ClickToggleToDeactivate")}
                  </span>
                </div>

                <div className="details-cell details-cell-4" />
              </div>
            </div>

            <div className="flex-d text-center" style={{ justifyContent: "center" }}>
              <span>
                <h5 className="text-muted" >
                  {site.display_name}
                </h5>
              </span>
            </div>

            <div className="details-row" style={{ justifyContent: "center" }}>
              <SteeringModeSelector
                preferredType={preferredEnum || "Idle"}
                isSteeringOn={!isIdle}
                onAttemptPick={handleAttemptPick}
                tradingId={tradingId}
              />
            </div>

          </Collapse>
        </CardBody>

        {updateError && <WarningAlert message={updateError} />}
      </Card>

      <ConfirmationModal
        isOpen={modalOpen}
        title={modalTitle}
        message={modalMessage}
        onConfirm={
          modalKind === "toggle"
            ? confirmToggle
            : modalKind === "pick"
              ? confirmPick
              : () => setModalOpen(false)
        }
        onCancel={() => {
          setPendingPickEnum(null);
          setModalOpen(false);
        }}
        confirmLabel={confirmLabel}
        cancelLabel={
          modalKind === "error"
            ? t("SteeringOverviewBlock-modal-OK")
            : t("SteeringOverviewBlock-modal-Cancel")
        }
        isError={modalKind === "error"}
        stylingPreset={isIdle ? "SteeringOverviewBlock-Optischedule" : "SteeringOverviewBlock-Idle"}
      />
    </Col>
  );
}
